import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useConnectivityListener } from "@/hooks/useConnectivityListener";
import { getCacheEntry, setCacheEntry } from "@/utils/versionedCache";
import { safeRandomUUID } from "@/utils/id";

const CACHE_SCHEMA = "connectivity";
const CACHE_VERSION = 1;
const PENDING_EDITS_KEY = "pending-edits";

export interface PendingEdit {
  id: string;
  type: string;
  payload: unknown;
  createdAt: string;
}

interface ConnectivityContextValue {
  isOnline: boolean;
  pendingEdits: PendingEdit[];
  savePendingEdit: (edit: Omit<PendingEdit, "id" | "createdAt">) => void;
  consumePendingEdits: (type?: string, predicate?: (edit: PendingEdit) => boolean) => PendingEdit[];
}

const ConnectivityContext = createContext<ConnectivityContextValue | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
  initialIsOnline?: boolean;
}

export function ConnectivityProvider({ children, initialIsOnline }: ProviderProps) {
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    initialIsOnline ?? (typeof navigator === "undefined" ? true : navigator.onLine)
  );
  const [pendingEdits, setPendingEdits] = useState<PendingEdit[]>(() =>
    getCacheEntry<PendingEdit[]>(CACHE_SCHEMA, CACHE_VERSION, PENDING_EDITS_KEY) || []
  );

  useConnectivityListener((status) => setIsOnline(status));

  useEffect(() => {
    setCacheEntry(CACHE_SCHEMA, CACHE_VERSION, PENDING_EDITS_KEY, pendingEdits);
  }, [pendingEdits]);

  const savePendingEdit = useCallback((edit: Omit<PendingEdit, "id" | "createdAt">) => {
    const entry: PendingEdit = {
      ...edit,
      id: safeRandomUUID(),
      createdAt: new Date().toISOString(),
    };
    setPendingEdits((prev) => [...prev, entry]);
  }, []);

  const consumePendingEdits = useCallback((type?: string, predicate?: (edit: PendingEdit) => boolean) => {
    let matches: PendingEdit[] = [];

    setPendingEdits((prev) => {
      const eligible = type ? prev.filter((edit) => edit.type === type) : [...prev];
      matches = predicate ? eligible.filter(predicate) : eligible;
      const matchIds = new Set(matches.map((edit) => edit.id));
      const remaining = prev.filter((edit) => !matchIds.has(edit.id));
      return remaining;
    });

    return matches;
  }, []);

  const value = useMemo(
    () => ({ isOnline, pendingEdits, savePendingEdit, consumePendingEdits }),
    [consumePendingEdits, isOnline, pendingEdits, savePendingEdit]
  );

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity() {
  const context = useContext(ConnectivityContext);
  if (!context) {
    throw new Error("useConnectivity must be used within a ConnectivityProvider");
  }
  return context;
}
